import React, { useEffect, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useTheme } from '../../contexts/ThemeProvider';
import { getBackgroundColor } from '../../utils/helpers';
import { useLocation } from 'react-router-dom';
import { DARK_THEME, PRED_ROUTE } from '../../utils/constants';
import "../NewChart/chart.scss";

const LineChart = ({ options, data, onFilterChange, contractAddress }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const location = useLocation();
  const exploreMarketCheck = location?.pathname === PRED_ROUTE;
  const [activeTimeframe, setActiveTimeframe] = useState("ALL");
  const timeframes = ["1H", "6H", "1D", "1W", "1M", "ALL"];
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const percentages = data?.map(item => item?.percentages).filter(Boolean) || [];

  const generateStaticTimeLabels = (timeframe) => {
    const now = new Date();
    const labels = [];

    switch (timeframe) {
      case "1H": {
        for (let i = 1; i <= 4; i++) {
          const time = new Date(now.getTime() - (15 * i * 60 * 1000));
          const timeStr = time.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          });
          labels.unshift(`${timeStr}`);
        }
        break;
      }
      case "6H": {
        for (let i = 1; i <= 5; i++) {
          const time = new Date(now.getTime() - (i * 60 * 60 * 1000));
          const timeStr = time.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          });
          labels.unshift(`${timeStr}`);
        }
        break;
      }
      case "1D": {
        for (let i = 1; i <= 4; i++) {
          const time = new Date(now.getTime() - (6 * i * 60 * 60 * 1000));
          const timeStr = time.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          });
          labels.unshift(`${timeStr}`);
        }
        break;
      }
      case "1W": {
        for (let i = 1; i <= 7; i++) {
          const time = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
          const date = time.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short"
          }).toUpperCase();
          labels.unshift(date);
        }
        break;
      }
      case "1M": {
        for (let i = 1; i <= 4; i++) {
          const time = new Date(now.getTime() - (i * 7 * 24 * 60 * 60 * 1000));
          const date = time.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short"
          }).toUpperCase();
          labels.unshift(date);
        }
        break;
      }
      case "ALL": {
        const currentYear = now.getFullYear();
        const monthsToShow = [0, 2, 4, 6, 8, 10];
        for (let i = 0; i < monthsToShow.length; i++) {
          const monthIndex = monthsToShow[i];
          const time = new Date(currentYear, monthIndex, 1);
          const monthName = time.toLocaleDateString("en-US", {
            month: "short"
          }).toUpperCase();
          labels.push(monthName);
        }
        break;
      }
      default: {
        return null;
      }
    }
    return labels;
  };
  const staticLabels = generateStaticTimeLabels(activeTimeframe);
  const labelCount = staticLabels?.length || 1;

  const handleTimeframeChange = (timeframe) => {
    setActiveTimeframe(timeframe);
    if (onFilterChange && contractAddress) {
      const filter = timeframe === "ALL" ? null : timeframe;
      onFilterChange(contractAddress, filter);
    }
  };


  const adjustedSeries = options?.map((option, index) => {
    const priceData = percentages?.[index];
    const dataPoints = priceData || [];
    const dataLength = dataPoints?.length;

    let mappedData;
    if (dataLength === 0) {
      mappedData = [
        { x: 0, y: 0 },
        { x: labelCount - 1, y: 0 }
      ];
    } else if (dataLength === 1) {
      mappedData = [
        { x: 0, y: dataPoints[0] },
        { x: labelCount - 1, y: dataPoints[0] }
      ];
    } else {
      mappedData = dataPoints?.map((value, i) => ({
        x: dataLength > 1 ? (i / (dataLength - 1)) * (labelCount - 1) : 0,
        y: value
      }));
    }

    return {
      name: option?.optionName || `Option ${index + 1}`,
      data: mappedData,
    };
  }) || [];

  const chartConfig = {
    series: adjustedSeries,
    options: {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: true, type: 'x' }
      },

      colors: percentages?.map((_, index) => getBackgroundColor(index % 26, theme, percentages?.length)),
      stroke: {
        width: 2,
        curve: 'smooth'
      },
      markers: {
        size: 0,
        hover: { size: 5 }
      },
      tooltip: {
        enabled: true,
        shared: true,
        followCursor: true,
        theme: 'light',
        x: {
          formatter: (value) => {
            const index = Math.round(value);
            return staticLabels?.[index] || '';
          }
        },
        y: {
          formatter: (val) => `${val?.toFixed(2)}%`
        }
      },

      grid: {
        borderColor: theme === DARK_THEME
          ? 'rgba(255,255,255,0.1)'
          : 'rgba(0,0,0,0.1)',
        strokeDashArray: 3
      },

      xaxis: {
        type: 'numeric',
        min: 0,
        max: labelCount - 1,
        tickAmount: labelCount - 1,
        labels: {
          show: true,
          hideOverlappingLabels: false,
          trim: false,
          rotate: 0,
          rotateAlways: false,
          offsetX: 3,
          offsetY: 0,
          formatter: (value) => {
            const index = Math.round(value);
            return staticLabels?.[index] || '';
          },
          style: {
            colors: theme === DARK_THEME ? 'white' : 'black',
            fontSize: '11px',
          }
        },
        axisBorder: { show: true },
        axisTicks: { show: true },
      },

      yaxis: {
        min: 0,
        opposite: true,
        labels: {
          formatter: (val) => `${val}%`,
          style: { colors: theme === DARK_THEME ? 'white' : 'black' }
        }
      },

      legend: {
        show: !isMobile,
        position: 'bottom',
        horizontalAlign: exploreMarketCheck ? 'center' : 'left',
        labels: {
          colors: theme === DARK_THEME ? 'white' : 'black'
        },
        formatter: (seriesName, opts) => {
          const seriesIndex = opts.seriesIndex;
          const lastValue =
            percentages?.[seriesIndex]?.[percentages[seriesIndex]?.length - 1];

          return `${seriesName} - ${(lastValue ?? 0).toFixed(2)}%`;
        },
        markers: {
          width: 10,
          height: 10,
          radius: 3,
          strokeWidth: 0
        }
      }

    }
  };


  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const onWheelCapture = (e) => {
      if (e.ctrlKey || e.metaKey) return;
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    };

    root.addEventListener('wheel', onWheelCapture, { capture: true, passive: true });

    return () => {
      root.removeEventListener('wheel', onWheelCapture, { capture: true });
    };
  }, []);
  return (
    <>
      {!exploreMarketCheck && (
        <div className="interactive-chart">
          <div className="chart-header">
            <div className="timeframe-buttons">
              {timeframes.map(tf => (
                <button
                  key={tf}
                  className={`timeframe-btn ${activeTimeframe === tf ? "active" : ""}`}
                  onClick={() => handleTimeframeChange(tf)}
                >
                  {tf}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div ref={containerRef} className="w-full">
        <ReactApexChart
          options={chartConfig.options}
          series={chartConfig.series}
          type="line"
          height={window.innerWidth > 991 ? 350 : 250}
        />
      </div>
    </>
  );
};

export default LineChart;
